import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AppDispatch } from "../../store/store";
import { RootState } from "../../store/reducers";
import {
  startImpersonation,
  stopImpersonationAction,
} from "../../store/modules/admin/adminActions";

/**
 * Was a text field inside the account dropdown, which left no room for labels or errors.
 * Stopping impersonation is also offered in the account menu, since it is needed mid-task.
 */
export default function ImpersonationPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { isImpersonating, impersonatedUserEmail } = useSelector((state: RootState) => state.admin);

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await dispatch(startImpersonation(email.trim()));
      setEmail("");
    } catch (thrown: any) {
      setError(`Impersonation failed: ${thrown?.message ?? "unknown error"}`);
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(stopImpersonationAction());
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    } catch (thrown: any) {
      setError(`Could not stop impersonation: ${thrown?.message ?? "unknown error"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 560 }}>
      <Typography variant="h6" component="h2">
        Impersonation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View the app as another user. Everything you do is recorded against your own admin account.
      </Typography>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isImpersonating ? (
        <Stack spacing={2}>
          <Alert severity="info" role="status">
            Currently impersonating <strong>{impersonatedUserEmail || "a user"}</strong>.
          </Alert>
          <Box>
            <Button color="error" variant="contained" disabled={busy} onClick={stop}>
              Stop impersonation
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void start();
          }}
        >
          <Stack spacing={2}>
            <TextField
              label="User email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
              slotProps={{ htmlInput: { autoCapitalize: "none", spellCheck: false } }}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={!email.trim() || busy}>
                Start impersonation
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
